/**
 * Orientação do dado e leitura da face do resultado.
 *
 * O cliente roda a física e, quando o corpo adormece, chama `readTopFace` para
 * saber qual face está para cima. O valor lido tem de bater com o valor que o
 * servidor sorteou — se não bater, o cliente re-lança (e, em último caso, usa
 * `snapToFace` para girar suavemente o dado até a face correta).
 */
import {
  add,
  cross,
  dot,
  normalize,
  scale,
  type DieGeometry,
  type Vec3,
} from './geometry.js';

export type Quat = readonly [number, number, number, number];

export const IDENTITY_QUAT: Quat = [0, 0, 0, 1];
export const WORLD_UP: Vec3 = [0, 1, 0];
const WORLD_DOWN: Vec3 = [0, -1, 0];

/** Direção que a face do resultado deve apontar ao parar. */
export function resultDirection(geometry: DieGeometry): Vec3 {
  return geometry.resultFaceIsUp ? WORLD_UP : WORLD_DOWN;
}

export function quatFromAxisAngle(axis: Vec3, radians: number): Quat {
  const unit = normalize(axis);
  const half = radians / 2;
  const sin = Math.sin(half);
  return [unit[0] * sin, unit[1] * sin, unit[2] * sin, Math.cos(half)];
}

export function quatMultiply(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

export function quatConjugate(q: Quat): Quat {
  return [-q[0], -q[1], -q[2], q[3]];
}

export function quatNormalize(q: Quat): Quat {
  const magnitude = Math.hypot(q[0], q[1], q[2], q[3]);
  if (magnitude === 0) return IDENTITY_QUAT;
  return [q[0] / magnitude, q[1] / magnitude, q[2] / magnitude, q[3] / magnitude];
}

/** Rotaciona um vetor por um quatérnion (v' = v + 2u × (u × v + w v)). */
export function quatRotate(q: Quat, v: Vec3): Vec3 {
  const u: Vec3 = [q[0], q[1], q[2]];
  const inner = add(cross(u, v), scale(v, q[3]));
  return add(v, scale(cross(u, inner), 2));
}

/** Menor rotação que leva `from` em `to`. */
export function quatFromTo(from: Vec3, to: Vec3): Quat {
  const a = normalize(from);
  const b = normalize(to);
  const cosine = Math.max(-1, Math.min(1, dot(a, b)));

  if (cosine > 0.999999) return IDENTITY_QUAT;
  if (cosine < -0.999999) {
    const helper: Vec3 = Math.abs(a[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    return quatFromAxisAngle(normalize(cross(a, helper)), Math.PI);
  }
  return quatFromAxisAngle(cross(a, b), Math.acos(cosine));
}

export type FaceReading = {
  value: number;
  faceIndex: number;
  /** 1 = face perfeitamente alinhada; abaixo de ~0.99 o dado não está plano */
  alignment: number;
  /** true somente se o dado está plano E sem empate entre duas faces */
  settled: boolean;
  /** true quando duas faces disputam a leitura (dado apoiado numa aresta) */
  ambiguous: boolean;
};

/**
 * Alinhamento mínimo para considerar o dado apoiado numa face.
 * cos(1.8°) ≈ 0.9995 — folga suficiente para o ruído do motor de física.
 */
export const SETTLED_MIN_ALIGNMENT = 0.9995;

/** Diferença mínima entre a melhor e a segunda melhor face para não haver empate. */
const AMBIGUITY_MARGIN = 1e-3;

/**
 * Qual face está apontando na direção do resultado.
 *
 * `settled` é o que o cliente deve consultar antes de aceitar o resultado: um d4
 * apoiado numa aresta tem duas faces empatadas, e ler "a que está mais para
 * baixo" nesse instante devolveria um número arbitrário.
 */
export function readTopFace(geometry: DieGeometry, orientation: Quat): FaceReading {
  const target = resultDirection(geometry);
  let bestIndex = 0;
  let bestAlignment = -Infinity;
  let secondAlignment = -Infinity;

  for (let index = 0; index < geometry.faces.length; index += 1) {
    const face = geometry.faces[index]!;
    const alignment = dot(quatRotate(orientation, face.normal), target);
    if (alignment > bestAlignment) {
      secondAlignment = bestAlignment;
      bestAlignment = alignment;
      bestIndex = index;
    } else if (alignment > secondAlignment) {
      secondAlignment = alignment;
    }
  }

  const ambiguous = bestAlignment - secondAlignment < AMBIGUITY_MARGIN;

  return {
    value: geometry.faces[bestIndex]!.value,
    faceIndex: bestIndex,
    alignment: bestAlignment,
    settled: bestAlignment >= SETTLED_MIN_ALIGNMENT && !ambiguous,
    ambiguous,
  };
}

/** O dado está plano e sem empate? Use antes de aceitar o resultado. */
export function isSettled(geometry: DieGeometry, orientation: Quat): boolean {
  return readTopFace(geometry, orientation).settled;
}

/** Índice da face com determinado valor. */
export function faceIndexByValue(geometry: DieGeometry, value: number): number {
  const index = geometry.faces.findIndex((face) => face.value === value);
  if (index < 0) throw new Error(`d${geometry.sides} não tem face com valor ${value}`);
  return index;
}

/** Rotação corretiva que deixa a face pedida apontando para o resultado. */
export function snapToFace(geometry: DieGeometry, orientation: Quat, faceIndex: number): Quat {
  const face = geometry.faces[faceIndex];
  if (!face) throw new Error(`face ${faceIndex} não existe no d${geometry.sides}`);
  const current = quatRotate(orientation, face.normal);
  const delta = quatFromTo(current, resultDirection(geometry));
  return quatNormalize(quatMultiply(delta, orientation));
}

/** Rotação corretiva a partir do valor sorteado pelo servidor. */
export function snapToValue(geometry: DieGeometry, orientation: Quat, value: number): Quat {
  return snapToFace(geometry, orientation, faceIndexByValue(geometry, value));
}
